import { PointWarningDialogType, PackageExpirationDialogType } from '@/interfaces/paid-management';
import Cookies from 'js-cookie';

export const getPointDaysLeft = (
  availablePoints: number,
  averageDailyConsumePoints: number,
): number => {
  if (availablePoints <= 0) return 0;
  if (!averageDailyConsumePoints || averageDailyConsumePoints === 0) return Infinity;
  return availablePoints / averageDailyConsumePoints;
};

export const getPointWarningDialogType = (daysLeft: number): PointWarningDialogType | null => {
  if (daysLeft <= 0) return 'insufficient';
  if (daysLeft < 1) return 'one-day-remaining';
  if (daysLeft < 3) return 'three-day-remaining';
  return null;
};

export const POINT_WARNING_COOKIES = {
  INSUFFICIENT: 'point_insufficient_warning_shown',
  ONE_DAY_REMAINING: 'point_one_day_warning_shown',
  THREE_DAY_REMAINING: 'point_three_day_warning_shown',
} as const;

export const getPointWarningCookieName = (type: PointWarningDialogType): string => {
  switch (type) {
    case 'insufficient':
      return POINT_WARNING_COOKIES.INSUFFICIENT;
    case 'one-day-remaining':
      return POINT_WARNING_COOKIES.ONE_DAY_REMAINING;
    case 'three-day-remaining':
      return POINT_WARNING_COOKIES.THREE_DAY_REMAINING;
    default:
      throw new Error(`Unknown warning type: ${type}`);
  }
};

export const clearAllPointWarningCookies = (): void => {
  Object.values(POINT_WARNING_COOKIES).forEach((cookieName) => {
    Cookies.remove(cookieName);
  });
};

/**
 * 计算剩余天数
 * @param expiredAt 过期时间戳（毫秒）
 * @returns 剩余天数，如果已过期返回 0，如果未设置返回 Infinity
 */
export const getDaysLeft = (expiredAt: number | undefined | null): number => {
  if (!expiredAt || expiredAt === 0) return Infinity;
  const msLeft = expiredAt - Date.now();
  if (msLeft <= 0) return 0;
  // 向上取整：只要剩余不足 1 天也算作 1 天 86400000 是一天的毫秒数
  return Math.ceil(msLeft / 86400000);
};

/**
 * 根据套餐剩余天数和状态获取弹窗类型
 * @param daysLeft 剩余天数
 * @param packageStatus 套餐状态
 * @returns 弹窗类型，如果不需要弹窗返回 null
 */
export const getPackageExpirationDialogType = (
  daysLeft: number,
  packageStatus?: string,
): PackageExpirationDialogType | null => {
  // 如果已到期，返回已到期类型（优先级最高）
  if (packageStatus === 'EXPIRED' || daysLeft <= 0) {
    return 'expired';
  }

  // 如果剩余1天或更少，返回1天到期类型（优先级中等）
  if (daysLeft <= 1) {
    return 'one-day-expiring';
  }

  // 如果剩余3天或更少，返回3天到期类型（优先级最低）
  if (daysLeft <= 3) {
    return 'three-day-expiring';
  }

  return null;
};

export const PACKAGE_EXPIRATION_COOKIES = {
  EXPIRED: 'package_expired_warning_shown',
  ONE_DAY_EXPIRING: 'package_one_day_expiring_warning_shown',
  THREE_DAY_EXPIRING: 'package_three_day_expiring_warning_shown',
} as const;

export const getPackageExpirationCookieName = (type: PackageExpirationDialogType): string => {
  switch (type) {
    case 'expired':
      return PACKAGE_EXPIRATION_COOKIES.EXPIRED;
    case 'one-day-expiring':
      return PACKAGE_EXPIRATION_COOKIES.ONE_DAY_EXPIRING;
    case 'three-day-expiring':
      return PACKAGE_EXPIRATION_COOKIES.THREE_DAY_EXPIRING;
    default:
      throw new Error(`Unknown expiration dialog type: ${type}`);
  }
};

export const clearAllPackageExpirationCookies = (): void => {
  Object.values(PACKAGE_EXPIRATION_COOKIES).forEach((cookieName) => {
    Cookies.remove(cookieName);
  });
};

/**
 * 格式化订单内容名称，提取商品名称（排除订购类型和版本）
 * @param name 内容名称 订购类型-商品名称-版本、来鼓2.0点数包-版本
 * @returns 商品名称
 */
export const formatOrderContentName = (name?: string) => {
  if (!name) return '--';
  const parts = name.split('-');
  // 显示最后一部分
  return parts[parts.length - 1];
};
